import { ChangeDetectionStrategy, Component, ElementRef, afterNextRender, computed, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IconComponent } from '@kin/spark-ui';
import { SignupAddressStore } from '../address/signup-address.store';
import { SignupResetService } from '../signup-reset.service';
import { ENROLLED_STATE } from './submitted-signup.service';

/**
 * Above /signup when Back from /enrolled restores the submitted page (frozen below; see SubmittedSignupService):
 * says it's already in, and offers the way on, to the confirmation or to another address (as Back does).
 */
@Component({
  selector: 'app-submitted-notice',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="notice" aria-labelledby="submitted-notice-title"
             [class.leaving]="reset.leaving()" [attr.inert]="reset.leaving() ? '' : null">
      <div class="badge" aria-hidden="true"><ngk-icon icon="check" size="20px" /></div>
      <div class="body">
        <!-- Focused on arrival (see constructor): the page below is inert, so this is where a screen reader starts -->
        <h1 id="submitted-notice-title" #heading tabindex="-1">{{ title() }}</h1>
        <p>{{ message() }}</p>
        <div class="actions">
          <button type="button" class="cta" (click)="continueToEnrolled()">
            <span>Continue</span>
            <ngk-icon class="cta-icon" icon="arrow-right" size="18px" />
          </button>
          <a class="another" [href]="reset.landingUrl" (click)="reset.onLandingLinkClick($event)">Enter another address</a>
        </div>
      </div>
    </section>
  `,
  styles: `
    /* So the notice and the frozen card line up */
    :host { display: block; max-width: 720px; margin: 0 auto; padding: 40px 24px 0; box-sizing: border-box; }
    :host(.narrow) { max-width: 640px; }

    @keyframes settle { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }

    /* The page's card surface (white, 24px radius); the one entrance on the page, above the frozen form */
    .notice {
      display: grid; grid-template-columns: 40px minmax(0, 1fr); gap: 20px;
      padding: 24px 28px; background: var(--kin-white); border-radius: var(--kin-radius-card);
      box-shadow: var(--kin-shadow-md); animation: settle 320ms var(--kin-ease-out);
      transition: opacity 120ms var(--kin-ease-out);
    }
    .notice.leaving { opacity: 0.55; pointer-events: none; }

    /* White check on Green-600 (5.6:1), as on /enrolled */
    .badge {
      display: flex; align-items: center; justify-content: center; width: 40px; height: 40px;
      border-radius: var(--kin-radius-pill); background: var(--kin-green-600); color: var(--kin-white);
    }

    /* Heading 3 / Body */
    h1 { font-size: 22px; font-weight: 600; line-height: 1.3; margin: 6px 0 6px; text-wrap: balance; }
    h1:focus { outline: none; }
    p { font-size: 16px; line-height: 1.6; color: var(--kin-text-secondary); margin: 0 0 20px; text-wrap: pretty; }

    .actions { display: flex; align-items: center; flex-wrap: wrap; gap: 12px 24px; }
    /* Solid Yellow-600 with Neutral-1000 text (12.20:1), like the page's CTAs; the arrow moves only on hover */
    .cta {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      min-height: 48px; padding: 12px 24px; border: 0; border-radius: var(--kin-radius-pill); cursor: pointer;
      font-family: inherit; font-size: 16px; font-weight: 600; line-height: 1.25;
      color: var(--kin-neutral-1000); background: var(--kin-yellow-600);
      transition: background 180ms var(--kin-ease-out);
    }
    .cta:hover { background: var(--kin-yellow-400); }
    .cta:active { background: var(--kin-yellow-700); }
    .cta:focus-visible { outline: none; box-shadow: var(--kin-shadow-focus); }
    .cta-icon { flex-shrink: 0; transition: transform 160ms var(--kin-ease-out); }
    .cta:hover .cta-icon { transform: translateX(3px); }
    /* The page's link style (Blue-600, underlined), at the weight of Back */
    .another { font-size: 16px; font-weight: 600; }

    @media (max-width: 600px) {
      :host { padding: 24px 16px 0; }
      .notice { grid-template-columns: 1fr; gap: 12px; padding: 24px; }
      h1 { margin-top: 0; }
      .actions { flex-direction: column; align-items: stretch; text-align: center; gap: 16px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .notice { animation: none; transition: none; }
      .cta, .cta-icon { transition: none; }
      .cta:hover .cta-icon { transform: none; }
    }
  `,
})
export class SubmittedNoticeComponent {
  private router = inject(Router);
  private store = inject(SignupAddressStore);
  protected reset = inject(SignupResetService);

  private readonly heading = viewChild.required<ElementRef<HTMLHeadingElement>>('heading');

  /** An enrollment or the waitlist, as submitted. */
  private readonly enrolled = this.store.isServiceable;
  private readonly address = computed(() => this.store.singleLineAddress() || 'your address');

  protected readonly title = computed(() => this.enrolled() ? "You've already enrolled." : "You're already on the waitlist.");
  protected readonly message = computed(() => this.enrolled()
    ? `We have your enrollment for ${this.address()}. You don't need to submit it again.`
    : `We'll email you when ${this.address()} is eligible. You don't need to sign up again.`);

  constructor() {
    // Focus moves the notice into view too, wherever the browser restored the scroll
    afterNextRender(() => this.heading().nativeElement.focus());
  }

  protected continueToEnrolled(): void {
    this.router.navigate(['/enrolled'], { state: { [ENROLLED_STATE]: true } });
  }
}
